var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import * as d3 from 'd3';
import { Canvas } from '../components/Canvas';
import { colorPalette as defaultPalette, defaultMargins } from '../utils';
import { EmptyGraph } from './components/EmptyGraph';
import { Line } from './components/Line';
import { multiYExtent } from './utils/multiYExtent';
import { useCharts } from '../components/Charts';
import { Legend } from '../components/Legend';
import { TrackArea } from '../TrackArea';
import { typedMemo } from '../../utils/components/typedMemo';
import { palette } from '../../constants';
import { IntervalGuides } from '../components/IntervalGuides';
import { GridRows } from '../Grid';
import { Axis } from '../Axis';
import { sortAlphabetically } from '../../utils';
var blankYScale = d3.scaleLinear();
/** Prevent unnecessary recalculation on higher-level renders */
var MemoedLine = typedMemo(Line);
var MemoedTrackArea = typedMemo(TrackArea);
function createLineGenerator(xScale, yScale, accessors) {
    var xAccessorDate = function (d) { return new Date(accessors.x(d)); };
    var xScaledAccessor = function (d) { var _a; return (_a = xScale(xAccessorDate(d))) !== null && _a !== void 0 ? _a : 0; };
    var yScaledAccessor = function (d) { var _a; return (_a = yScale(accessors.y(d))) !== null && _a !== void 0 ? _a : 0; };
    return d3.line().x(xScaledAccessor).y(yScaledAccessor);
}
export function Lines(_a) {
    var name = _a.name, series = _a.series, accessors = _a.accessors, height = _a.height, xDomain = _a.xDomain, defaultActive = _a.defaultActive, meta = _a.meta, _b = _a.colors, colors = _b === void 0 ? defaultPalette : _b, marginProps = _a.margins, _c = _a.emptyGraphMessage, emptyGraphMessage = _c === void 0 ? '' : _c, _d = _a.numTicks, numTicks = _d === void 0 ? 6 : _d, _e = _a.hideTicks, hideTicks = _e === void 0 ? true : _e, tooltip = _a.tooltip, _f = _a.axesProps, axesProps = _f === void 0 ? {} : _f;
    var width = useCharts().width;
    var margins = defaultMargins(marginProps);
    var _g = React.useState(undefined), hovered = _g[0], setHovered = _g[1];
    var boundedWidth = width - margins.left - margins.right;
    var valid = React.useMemo(function () {
        return Object.values(series).filter(function (s) {
            return accessors.valid !== undefined
                ? accessors.valid(s) === 'valid' ||
                    accessors.valid(s) === 'incomplete'
                : true;
        });
    }, [series, accessors.valid]);
    var _h = React.useState(new Set(defaultActive !== null && defaultActive !== void 0 ? defaultActive : valid.map(function (d) { return accessors.key(d); }))), activeSeries = _h[0], setActiveSeries = _h[1];
    var active = React.useMemo(function () { return valid.filter(function (s) { return activeSeries.has(accessors.key(s)); }); }, [valid, activeSeries]);
    var yDomain = React.useMemo(function () { return multiYExtent(valid, accessors, activeSeries); }, [valid, accessors, activeSeries]);
    var xScale = React.useMemo(function () { return d3.scaleTime().domain(xDomain).range([0, boundedWidth]); }, [xDomain, boundedWidth]);
    var yScale = React.useMemo(function () {
        return yDomain !== undefined
            ? d3
                .scaleLinear()
                .domain(yDomain)
                .range([height - margins.top - margins.bottom, 0])
                .nice()
            : undefined;
    }, [yDomain, height]);
    var lineGenerator = React.useMemo(function () {
        return yScale !== undefined
            ? createLineGenerator(xScale, yScale, accessors)
            : undefined;
    }, [xScale, yScale, accessors]);
    var colorScheme = React.useMemo(function () {
        // NOTE: sorting the keys here ensures that the property order of the
        // incoming series does not affect the color scale.
        // This can happen if the series keys are the same, but their order
        // on the series object changes between updates.
        var keys = Object.values(series).map(function (d) { return accessors.key(d); });
        return d3
            .scaleOrdinal()
            .domain(sortAlphabetically(keys))
            .range(colors);
    }, [series, colors]);
    var axisConfigs = React.useMemo(function () {
        var _a, _b;
        var shared = { numTicks: numTicks, hideTicks: hideTicks, margins: margins };
        var left = __assign(__assign(__assign({}, shared), { orientation: 'left', scale: (_a = yScale === null || yScale === void 0 ? void 0 : yScale.nice()) !== null && _a !== void 0 ? _a : blankYScale, isCanvasChild: true, height: height,
            margins: margins }), axesProps.left);
        var top = __assign(__assign(__assign({}, shared), { orientation: 'top', scale: xScale.nice(), height: 40, hide: true, margins: __assign(__assign({}, margins), { top: 0 }) }), axesProps.top);
        var right = __assign(__assign(__assign({}, shared), { orientation: 'right', scale: (_b = yScale === null || yScale === void 0 ? void 0 : yScale.nice()) !== null && _b !== void 0 ? _b : blankYScale, isCanvasChild: true, height: height, hide: true }), axesProps.right);
        var bottom = __assign(__assign(__assign({}, shared), { orientation: 'bottom', scale: xScale.nice(), height: 40, isCanvasChild: true }), axesProps.bottom);
        return {
            left: left,
            bottom: bottom,
            right: right,
            top: top
        };
    }, [axesProps, margins]);
    var clearHovered = React.useCallback(function () {
        setHovered(undefined);
    }, []);
    var handleHover = React.useCallback(function (id) {
        if (activeSeries.has(id)) {
            setHovered(id);
        }
    }, [activeSeries]);
    // Prevent hover effect from persisting after user has
    // deactivated a series (i.e., clicked on a legend chip)
    var handleLegendClick = React.useCallback(function (active, id) {
        if (id !== undefined && active.has(id)) {
            setHovered(id);
        }
        else {
            setHovered(undefined);
        }
        setActiveSeries(active);
    }, []);
    // Series data of chip currently being hovered, but only
    // if series is active
    var hoveredSeries = React.useMemo(function () {
        if (hovered === undefined || series[hovered] === undefined)
            return;
        var s = series[hovered];
        var key = accessors.key(s);
        return activeSeries.has(key) ? s : undefined;
    }, [hovered, series, accessors, activeSeries]);
    if (yScale === undefined || lineGenerator === undefined)
        return (React.createElement(EmptyGraph, { series: series, accessors: accessors, colorScheme: colorScheme, height: height, margins: margins, message: emptyGraphMessage, axesProps: axisConfigs }));
    return (React.createElement(React.Fragment, null,
        active.length > 0 ? (React.createElement(React.Fragment, null,
            React.createElement(Axis, __assign({}, axisConfigs.top)),
            React.createElement(Canvas, { margins: margins, height: height },
                React.createElement(Axis, __assign({}, axisConfigs.right)),
                React.createElement(Axis, __assign({}, axisConfigs.left)),
                React.createElement(IntervalGuides, { xScale: xScale }),
                React.createElement(GridRows, { scale: yScale, numTicks: numTicks }),
                active.map(function (s) { return (React.createElement(MemoedLine, { key: accessors.key(s), data: accessors.data(s), lineGenerator: lineGenerator, stroke: hovered === undefined
                        ? colorScheme(accessors.key(s))
                        : palette.neutral4, opacity: hovered === undefined ? 1 : 0.4 })); }),
                hoveredSeries !== undefined ? (React.createElement(MemoedLine, { key: accessors.key(hoveredSeries), data: accessors.data(hoveredSeries), lineGenerator: lineGenerator, stroke: colorScheme(accessors.key(hoveredSeries)), opacity: 1 })) : null,
                React.createElement(MemoedTrackArea, { pinId: name, height: height, series: active, accessors: accessors, xScale: xScale, meta: meta, colorScheme: colorScheme, tooltip: tooltip }),
                React.createElement(Axis, __assign({}, axisConfigs.bottom))))) : (React.createElement(React.Fragment, null,
            React.createElement(EmptyGraph, { accessors: accessors, colorScheme: colorScheme, height: height, margins: margins, message: emptyGraphMessage, axesProps: axisConfigs }))),
        React.createElement(Legend, { activeSet: activeSeries, series: series, accessors: accessors, colorScheme: colorScheme, onToggle: handleLegendClick, onMouseEnter: handleHover, onMouseLeave: clearHovered, marginLeft: margins === null || margins === void 0 ? void 0 : margins.left, marginRight: margins === null || margins === void 0 ? void 0 : margins.right })));
}
//# sourceMappingURL=Lines.js.map